import type { QueryClient } from '@tanstack/react-query';
import {
  questionByUuidKey,
  questionDetailKey,
  questionsBaseKey,
  questionsMeetverseKey,
  questionsEventKey,
  resolveQuestionBaseKey,
  resolveQuestionKey,
  userQuestionsBaseKey,
} from './queryKeys';

export interface InvalidateQuestionCachesOptions {
  queryClient: QueryClient;
  eventIds: string[];
  slug?: string | null;
  shouldInvalidateMeetverse?: boolean;
  invalidateUserQuestions?: boolean;
}

// Centralized invalidation replacing ~4 identical inline callbacks in
// QuestionCard, ProfileQuestionCard, QuestionDetails, and
// useQuestionInteractionsOptimistic.
export const invalidateQuestionCaches = (options: InvalidateQuestionCachesOptions) => {
  const { queryClient, eventIds, slug, shouldInvalidateMeetverse, invalidateUserQuestions } =
    options;

  void queryClient.invalidateQueries({ queryKey: questionsBaseKey() });

  if (shouldInvalidateMeetverse) {
    void queryClient.invalidateQueries({ queryKey: questionsMeetverseKey() });
  }

  eventIds.forEach(eventId => {
    void queryClient.invalidateQueries({ queryKey: questionsEventKey(eventId) });
  });

  if (slug) {
    void queryClient.invalidateQueries({ queryKey: resolveQuestionKey(slug) });
  }

  if (invalidateUserQuestions) {
    void queryClient.invalidateQueries({ queryKey: userQuestionsBaseKey() });
  }
};

/**
 * `slug` for a caller that cannot name the post's slug and needs every slug-routed post page
 * swept. A symbol, so no real slug can be mistaken for it.
 */
export const EVERY_QUESTION_SLUG = Symbol('every question slug');

export interface QuestionDetailSweep {
  /**
   * The post's uuid. Sweeps the id-keyed detail (and its subkeys) and the post page opened by
   * uuid. Left out (a truncated event), only the slug route below is swept.
   */
  questionId: string | null | undefined;
  /**
   * Which slug-routed post pages to sweep. Required, so every caller says which it means:
   * - a slug: that page only;
   * - `EVERY_QUESTION_SLUG`: every slug-routed page, for a caller that cannot name the slug.
   *   Only mounted pages refetch;
   * - `null`: none. For a caller that must not touch a slug it cannot vouch for: a slug the post
   *   no longer answers to is a 404, which is final (`isPermanentQuestionReadFailure`), so a
   *   sweep of it reads nothing fresh and only puts a failed read behind whatever page is on it.
   */
  slug: string | null | typeof EVERY_QUESTION_SLUG;
  /**
   * `'none'`: mark the reads stale and fetch nothing now, not even a mounted one. Each is read
   * again the next time it mounts. For a caller that has just written the fresh post into the
   * entity store and is about to leave the page. Left out, mounted reads are fetched at once.
   */
  refetchType?: 'none';
}

/**
 * Marks one post's detail reads stale on every route that can show it. The post is read under
 * three keys, and a sweep that names fewer leaves one of its pages on the old post:
 * - `questionDetailKey(id)`: the id-keyed detail and its subkeys (suggested helpers);
 * - `questionByUuidKey(id)`: the post page opened by uuid (`useQuestionBySlugOrId`);
 * - `resolveQuestionKey(slug)`: the post page opened by slug (same hook).
 *
 * Resolves once every sweep it started has settled, so a caller that must show fresh data next
 * and has no other source for it can wait. Everyone else ignores the promise.
 */
export const invalidateQuestionDetail = async (
  queryClient: QueryClient,
  { questionId, slug, refetchType }: QuestionDetailSweep,
): Promise<void> => {
  const sweep = (queryKey: readonly unknown[]) =>
    queryClient.invalidateQueries(refetchType ? { queryKey, refetchType } : { queryKey });
  const sweeps: Promise<void>[] = [];
  if (questionId) {
    sweeps.push(sweep(questionDetailKey(questionId)));
    sweeps.push(sweep(questionByUuidKey(questionId)));
  }
  if (slug === EVERY_QUESTION_SLUG) {
    sweeps.push(sweep(resolveQuestionBaseKey()));
  } else if (slug) {
    sweeps.push(sweep(resolveQuestionKey(slug)));
  }
  await Promise.all(sweeps);
};
